import UtilityBar from '../components/layout/UtilityBar';
import Nav from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import LincolnAvatar from '../components/ui/LincolnAvatar';
import VoiceDemo from '../components/ui/VoiceDemo';
import Stat from '../components/ui/Stat';
import StudentSuccessCard from '../components/ui/StudentSuccessCard';
import FeatureBento from '../components/common/FeatureBento';
import FeaturedCourses from '../components/common/FeaturedCourses';
import CaseStudiesGrid from '../components/common/CaseStudiesGrid';
import { STUDENTS } from '../data/students';
import { Link } from 'react-router-dom';

/**
 * Homepage — "Voice-Native School" (V1).
 * Hero: big copy + voice orb + tap-to-talk demo.
 * Sections: two-audience split, how-it-works (providers), feature bento,
 * featured courses, case studies grid, student success, stats + CTA.
 */
export default function Home() {
  return (
    <div style={{ background: 'var(--bg)' }}>
      <UtilityBar />
      <Nav variant="light" />

      {/* ════════ HERO ════════ */}
      <section
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #0b1f38 0%, #102a4a 50%, #1a3a62 100%)',
          paddingTop: 56, paddingBottom: 72,
        }}
      >
        <div className="absolute inset-0"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(ellipse at 50% 40%, #000 30%, transparent 75%)',
            WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, #000 30%, transparent 75%)',
          }}
        />
        <div className="absolute pointer-events-none rounded-full"
          style={{ top: '10%', left: '50%', transform: 'translateX(-50%)', width: 800, height: 800, background: 'radial-gradient(circle, rgba(240,184,12,0.12) 0%, transparent 60%)' }}
        />

        <div className="lc-container on-navy relative" style={{ zIndex: 1 }}>
          <div className="grid items-center" style={{ gridTemplateColumns: '1.05fr 1fr', gap: 80 }}>
            <div>
              <div className="inline-flex items-center gap-2.5 rounded-full font-semibold tracking-widest"
                style={{ padding: '8px 14px', background: 'rgba(240,184,12,0.12)', border: '1px solid rgba(240,184,12,0.3)', fontSize: 12, color: 'var(--yellow-2)' }}>
                <span className="inline-block rounded-full" style={{ width: 6, height: 6, background: 'var(--yellow)' }} />
                INTRODUCING LINCOLN
              </div>
              <h1 className="text-white mt-6 mb-6" style={{ fontSize: 84, letterSpacing: '-0.03em' }}>
                A school<br />
                <span className="relative inline-block">
                  <span style={{ color: 'var(--yellow)' }}>you can talk to.</span>
                  <svg width="100%" height="14" viewBox="0 0 400 14" preserveAspectRatio="none" className="absolute" style={{ left: 0, bottom: -4 }}>
                    <path d="M2 8 Q 100 0 200 6 T 398 8" fill="none" stroke="var(--yellow)" strokeWidth="3" strokeLinecap="round" opacity="0.6" />
                  </svg>
                </span>{' '}
              </h1>
              <p style={{ fontSize: 20, color: 'rgba(255,255,255,0.75)', maxWidth: 540, lineHeight: 1.55, marginBottom: 36 }}>
                Skip the typing. Skip the forms. Just talk. Lincoln teaches every subject in 3 languages, and certifies what you learn.
              </p>
              <div className="flex flex-wrap gap-3.5 mb-10">
                <Link to="/providers" className="lc-btn ghost-light lg">For Schools &amp; Providers →</Link>
              </div>

              <div>
                <div className="uppercase tracking-widest mb-3.5" style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Trusted by</div>
                <div className="flex items-center flex-wrap" style={{ gap: 32 }}>
                  {['World Economic Forum', 'Republic of Malta', 'Finland · OPH', 'City of Fontana', 'Glowie'].map((t) => (
                    <div key={t} className="font-serif font-medium" style={{ fontSize: 17, color: 'rgba(255,255,255,0.7)' }}>{t}</div>
                  ))}
                </div>
              </div>
            </div>

            {/* Voice orb + demo */}
            <div className="flex flex-col items-center gap-6">
              <LincolnAvatar size={220} pulse withMicChip={false} />
              <VoiceDemo />
            </div>
          </div>
        </div>
      </section>

      {/* ════════ TWO-AUDIENCE SPLIT ════════ */}
      <section className="lc-section">
        <div className="lc-container">
          <div className="text-center mb-14">
            <span className="lc-eyebrow">Two ways in</span>
            <h2 className="mt-3.5">One school. Two audiences.</h2>
            <p className="mx-auto mt-3.5" style={{ color: 'var(--muted)', fontSize: 18, maxWidth: 580 }}>
              Whether you want to learn or whether you want to teach the world — Lincoln has a door for you.
            </p>
          </div>

          <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 28 }}>
            <AudienceCard
              eyebrow="🎓 FOR LEARNERS"
              eyebrowBg="rgba(240,184,12,0.25)"
              eyebrowColor="#8a6b00"
              cardBg="linear-gradient(135deg, #fff8e1 0%, #fff3c9 100%)"
              cardBorder="1px solid rgba(240,184,12,0.3)"
              bubbleBg="rgba(240,184,12,0.3)"
              title="I want to learn something new."
              desc="Talk to Lincoln, take auto-generated quizzes, earn certificates with QR codes you can share. Free for the world."
              bullets={[
                ['💬', 'Voice tutoring · 3 languages'],
                ['📝', 'Quizzes & flashcards generated for you'],
                ['🏅', 'QR-coded certificates & digital badges'],
                ['📺', 'YouTube + podcast worksheets'],
              ]}
              ctaTo="/courses"
              ctaLabel="Start learning — it's free →"
              ctaClass="yellow"
              dark={false}
            />
            <AudienceCard
              eyebrow="FOR PROVIDERS"
              eyebrowBg="rgba(34,181,114,0.18)"
              eyebrowColor="#7be3b0"
              cardBg="linear-gradient(135deg, #0b1f38 0%, #1a3a62 100%)"
              bubbleBg="rgba(34,181,114,0.25)"
              title="I want to launch my own course."
              desc="Drop your materials, Lincoln does the rest — voice tutor, quizzes, flashcards, certificates, multilingual delivery."
              bullets={[
                ['📂', 'Upload any material — PDFs, videos, slides'],
                ['🎙', 'Lincoln teaches it as a voice tutor'],
                ['📊', 'Auto-quizzes, flashcards, progress tracking'],
                ['🏆', 'Branded certificates with verifiable QR'],
              ]}
              ctaTo="/providers"
              ctaLabel="Become a provider →"
              ctaClass="primary"
              dark
            />
          </div>
        </div>
      </section>

      {/* ════════ HOW IT WORKS (PROVIDERS) ════════ */}
      <section className="lc-section" style={{ background: 'var(--bg-cream)' }}>
        <div className="lc-container">
          <div className="text-center" style={{ marginBottom: 64 }}>
            <span className="lc-eyebrow">For education providers</span>
            <h2 className="mt-3.5">From your course to a Lincoln tutor — in four steps.</h2>
          </div>

          <div className="relative">
            <div className="absolute" style={{ top: 56, left: '12.5%', right: '12.5%', height: 2, backgroundImage: 'linear-gradient(90deg, var(--yellow) 60%, transparent 60%)', backgroundSize: '12px 2px', zIndex: 0 }} />
            <div className="grid relative" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 24, zIndex: 1 }}>
              {[
                { n: '01', title: 'Upload your material',  desc: 'Drop in PDFs, slides, videos, podcasts, or any URL. Lincoln ingests it all.', kind: 'upload' },
                { n: '02', title: 'Lincoln learns it',     desc: 'Within minutes, Lincoln has built a voice-tutor expert in your content, in 3 languages.', kind: 'brain' },
                { n: '03', title: 'Auto-generate everything', desc: 'Quizzes, flashcards, worksheets, podcasts — all created and personalized per learner.', kind: 'gear' },
                { n: '04', title: 'Certify with QR',       desc: 'Issue branded certificates and digital badges — each verifiable by scanning a QR code.', kind: 'cert' },
              ].map((s) => (
                <div key={s.n} className="text-center">
                  <div className="relative inline-flex items-center justify-center bg-white mx-auto"
                    style={{ width: 112, height: 112, borderRadius: '50%', border: '3px solid var(--yellow)', boxShadow: 'var(--shadow-md)' }}>
                    <StepIllustration kind={s.kind} />
                    <span className="absolute inline-flex items-center justify-center font-serif font-bold"
                      style={{ top: -8, right: -8, width: 36, height: 36, borderRadius: '50%', background: 'var(--navy)', color: 'var(--yellow)', fontSize: 14 }}>
                      {s.n}
                    </span>
                  </div>
                  <h4 className="mt-5" style={{ fontSize: 21 }}>{s.title}</h4>
                  <p className="mx-auto mt-2.5 leading-relaxed" style={{ color: 'var(--muted)', fontSize: 15, maxWidth: 240 }}>{s.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center" style={{ marginTop: 64 }}>
            <a href="#" className="lc-link ml-6" style={{ color: 'var(--ink-2)' }}>Talk to our team →</a>
          </div>
        </div>
      </section>

      <FeatureBento />
      <FeaturedCourses />
      <CaseStudiesGrid />

      {/* ════════ SUCCESS STORIES ════════ */}
      <section className="lc-section" style={{ background: 'var(--bg-cream)' }}>
        <div className="lc-container">
          <div className="flex justify-between items-end mb-10">
            <div>
              <span className="lc-eyebrow">Success stories</span>
              <h2 className="mt-3.5">Real students. Real lift.</h2>
            </div>
            <a href="#" className="lc-link" style={{ color: 'var(--ink-2)' }}>See all 1,200 stories →</a>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 }}>
            {STUDENTS.map((s) => <StudentSuccessCard key={s.name} {...s} />)}
          </div>
        </div>
      </section>

      {/* ════════ STATS + CTA ════════ */}
      <section className="on-navy" style={{ background: 'var(--navy)', padding: '72px 0' }}>
        <div className="lc-container">
          <div className="grid items-center" style={{ gridTemplateColumns: '1.2fr 1fr', gap: 80 }}>
            <div>
              <span className="lc-eyebrow on-dark">By the numbers</span>
              <h2 className="text-white mt-3.5" style={{ fontSize: 56 }}>A school the size of a country — and growing.</h2>
              <p className="mt-4.5" style={{ marginTop: 18, fontSize: 18, color: 'rgba(255,255,255,0.7)', maxWidth: 480 }}>
                Lincoln is used by national education ministries, vocational schools, individual tutors, and millions of independent learners.
              </p>
            </div>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 36 }}>
              <Stat value="10" label="Countries" />
              <Stat value="55k" label="Active learners" color="#7be3b0" />
              <Stat value="3" label="Languages spoken" />
              <Stat value="98%" label="Cert. completion rate" color="#7be3b0" />
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ─── Two-audience card ──────────────────────────────────────────────────── */
function AudienceCard({ eyebrow, eyebrowBg, eyebrowColor, cardBg, cardBorder, bubbleBg, title, desc, bullets, ctaTo, ctaLabel, ctaClass, dark }) {
  return (
    <div className="relative overflow-hidden"
      style={{ borderRadius: 28, padding: 48, minHeight: 460, background: cardBg, border: cardBorder, color: dark ? '#fff' : 'inherit' }}>
      <div className="absolute rounded-full"
        style={{ top: -60, right: -60, width: 220, height: 220, background: bubbleBg }} />
      <div className="relative" style={{ zIndex: 1 }}>
        <div className="inline-flex items-center gap-2 font-bold tracking-wider mb-5 rounded-full"
          style={{ padding: '6px 12px', background: eyebrowBg, color: eyebrowColor, fontSize: 12 }}>
          {eyebrow}
        </div>
        <h3 className="mb-3.5" style={{ fontSize: 38, color: dark ? '#fff' : 'var(--ink)' }}>{title}</h3>
        <p className="mb-7" style={{ fontSize: 16, color: dark ? 'rgba(255,255,255,0.75)' : 'var(--ink-2)', maxWidth: 440 }}>{desc}</p>
        <ul className="list-none p-0 flex flex-col gap-2.5 mb-8">
          {bullets.map(([icon, text]) => (
            <li key={text} className="flex items-center gap-2.5" style={{ fontSize: 15, color: dark ? 'rgba(255,255,255,0.85)' : 'var(--ink-2)' }}>
              <span className="inline-flex items-center justify-center rounded-lg"
                style={{ width: 28, height: 28, background: dark ? 'rgba(34,181,114,0.2)' : 'rgba(240,184,12,0.25)' }}>{icon}</span>
              {text}
            </li>
          ))}
        </ul>
        <Link to={ctaTo} className={`lc-btn ${ctaClass}`}>{ctaLabel}</Link>
      </div>
    </div>
  );
}

/* ─── Step illustration icon set ─────────────────────────────────────────── */
function StepIllustration({ kind }) {
  const c = '#0b1f38';
  if (kind === 'upload') return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      <path d="M14 36v6a2 2 0 0 0 2 2h24a2 2 0 0 0 2-2v-6" stroke={c} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M28 12v22M19 22l9-10 9 10" stroke={c} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="10" y="6" width="14" height="4" rx="1" fill="#f0b80c" />
    </svg>
  );
  if (kind === 'brain') return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      <path d="M20 14a6 6 0 0 0-6 6c-3 0-5 3-5 6 0 2 1 4 3 5-1 2-1 4 0 6 1 2 3 3 5 3 0 4 3 6 7 6h12c4 0 7-2 7-6 2 0 4-1 5-3 1-2 1-4 0-6 2-1 3-3 3-5 0-3-2-6-5-6a6 6 0 0 0-6-6 7 7 0 0 0-7-3 7 7 0 0 0-7 3z" stroke={c} strokeWidth="2.2" />
      <circle cx="22" cy="24" r="2" fill="#22b572" />
      <circle cx="34" cy="24" r="2" fill="#22b572" />
      <path d="M22 30c2 2 6 2 8 0" stroke="#22b572" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
  if (kind === 'gear') return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      <circle cx="28" cy="28" r="10" stroke={c} strokeWidth="2.5" />
      <circle cx="28" cy="28" r="4" fill="#f0b80c" />
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <rect key={i} x="26" y="6" width="4" height="8" fill={c} transform={`rotate(${i * 60} 28 28)`} />
      ))}
    </svg>
  );
  return (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
      <rect x="8" y="10" width="40" height="28" rx="3" stroke={c} strokeWidth="2.5" fill="#fff" />
      <circle cx="28" cy="24" r="4" fill="#f0b80c" />
      <path d="M14 32h28" stroke={c} strokeWidth="1.5" />
      <rect x="34" y="40" width="12" height="12" fill={c} />
      <rect x="36" y="42" width="3" height="3" fill="#fff" />
      <rect x="41" y="42" width="3" height="3" fill="#fff" />
      <rect x="36" y="47" width="3" height="3" fill="#fff" />
      <rect x="41" y="47" width="3" height="3" fill="#fff" />
    </svg>
  );
}
